import { useState } from 'react';
import { Quotes } from '@phosphor-icons/react';
import { TESTIMONIALS, type Testimonial } from '../../data/landing';
import { StarRating } from './StarRating';
import { initials } from '../../lib/format';
import { Reveal } from '../../lib/motion';

interface TestimonialCarouselProps {
  testimonials?: Testimonial[];
}

/**
 * Social proof — Warm Editorial. A three-up wall of guest quotes on a cream
 * ground, framed as voices rather than star-ratings marketing. (Name retained
 * for the stable HomePage import; renders a grid, not a carousel.)
 */
export function TestimonialCarousel({
  testimonials = TESTIMONIALS,
}: TestimonialCarouselProps) {
  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-h2"
      className="bg-cream"
    >
      <div className="px-6 py-20 sm:px-10 lg:px-16 lg:py-28 2xl:px-24">
        <div>
          <h2
            id="testimonials-h2"
            className="text-balance font-serif text-[2.4rem] font-black leading-[0.98] tracking-tight text-ink sm:text-5xl xl:whitespace-nowrap"
          >
            Travellers who found their place.
          </h2>
        </div>

        <ul role="list" className="mt-14 grid grid-cols-1 gap-7 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <Reveal as="li" key={t.id} delay={i * 120} className="h-full">
              <TestimonialCard t={t} />
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

function TestimonialCard({ t }: { t: Testimonial }) {
  const [err, setErr] = useState(false);
  return (
    <div className="flex h-full flex-col rounded-[1.5rem] border border-sand bg-white p-7 transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-32px_rgba(28,26,23,0.5)]">
      <Quotes className="h-8 w-8 shrink-0 text-clay/40" aria-hidden="true" />
      <blockquote className="mt-4 flex-1 font-serif text-lg leading-relaxed text-ink-soft">
        “{t.quote}”
      </blockquote>

      <div className="mt-6 flex items-center gap-3 border-t border-sand pt-5">
        <span className="h-11 w-11 shrink-0 overflow-hidden rounded-full ring-2 ring-clay-light">
          {err ? (
            <span className="flex h-full w-full items-center justify-center bg-clay-light text-xs font-bold text-clay">
              {initials(t.authorName)}
            </span>
          ) : (
            <img
              src={t.avatarUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              onError={() => setErr(true)}
              className="h-full w-full object-cover"
            />
          )}
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink">{t.authorName}</p>
          <p className="truncate text-xs text-ink-muted">{t.authorLocation}</p>
        </div>
        <div className="ml-auto">
          <StarRating rating={t.rating} compact />
        </div>
      </div>
    </div>
  );
}
